import { useMemo } from "react";
import { useTokenInvitationCtx } from "./useTokenInvitationCtx";
import { useServerInvitation } from "./useServerInvitation";
import { decodeToken } from "./useInvitationToken";
import { InvitationItem } from "../../types/InvitationItem";

/**
 * Hook that combines token and server invitations with deduplication
 * Server invitations take precedence over token duplicates
 */
export function useCombinedInvitations() {
  const { tokens, respondToToken } = useTokenInvitationCtx();
  const {
    invitations: serverInvitations,
    isLoading,
    error,
    respondToServerInvitation,
  } = useServerInvitation();

  const combinedInvitations: InvitationItem[] = useMemo(() => {
    const result: InvitationItem[] = [];
    const seenInvitationIds = new Set<string>();

    // Add server invitations first (they are authoritative)
    for (const invitation of serverInvitations) {
      result.push({ source: "server", invitation });
      seenInvitationIds.add(invitation.invitation_id);
    }

    // Add token invitations only if not already seen in server invitations
    for (const token of tokens) {
      const invitation = decodeToken(token);
      if (invitation && !seenInvitationIds.has(invitation.invitation_id)) {
        result.push({ source: "token", token, invitation });
        seenInvitationIds.add(invitation.invitation_id);
      }
    }

    return result;
  }, [serverInvitations, tokens]);

  const respondToInvitation = async (
    item: InvitationItem,
    accept: boolean
  ): Promise<boolean> => {
    if (item.source === "token") {
      return await respondToToken(item.token, accept);
    } else {
      return await respondToServerInvitation(item.invitation, accept);
    }
  };

  return {
    allInvitations: combinedInvitations,
    respondToInvitation,
    isLoading,
    error,
    // Legacy interface for backward compatibility
    tokens,
    respondToToken,
  };
}
